import React, { useState, useEffect } from 'react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Profile } from './useProfiles';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface ProfileSelectorProps {
  onSelectProfile: (profileId: string) => void;
  selectedProfileId?: string;
}

const ProfileSelector: React.FC<ProfileSelectorProps> = ({ 
  onSelectProfile, 
  selectedProfileId 
}) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        
        const { data: session } = await supabase.auth.getSession();
        
        if (!session.session) {
          setLoading(false);
          return;
        }
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', session.session.user.id);
          
        if (error) {
          throw error;
        }
        
        setProfiles(data || []);
        
        // Auto-select the first profile if none is selected
        if (data && data.length > 0 && !selectedProfileId) {
          onSelectProfile(data[0].id);
        }
      } catch (error: any) {
        console.error('Error fetching profiles:', error);
        toast.error(t('profile.selector.loadError', { error: error.message }));
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfiles();
  }, [onSelectProfile, selectedProfileId, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-2">
        <Loader2 className="w-5 h-5 text-sangre-500 animate-spin" />
        <span className="ml-2 text-sm text-gray-600">{t('profile.selector.loading')}</span>
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <div className="text-center py-2 text-sm text-gray-600">
        {t('profile.selector.noProfiles')}
      </div>
    );
  }

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t('profile.selector.selectProfile')}
      </label>
      <Select 
        value={selectedProfileId} 
        onValueChange={onSelectProfile}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t('profile.selector.selectProfile')} />
        </SelectTrigger>
        <SelectContent>
          {profiles.map(profile => (
            <SelectItem key={profile.id} value={profile.id}>
              {profile.name} ({t('profile.selector.ageFormat', { age: profile.age })})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default ProfileSelector;
